import * as React from 'react';
import Box from '@mui/material/Box';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { PrimaryOutlinedButton } from 'components';
import { EditIcon } from 'assets/icons';
import {
  CertificateTemplate,
  ICertificate,
} from '../../../../../interface';
import CertificateModalForm from '../certificate-modal-form';
import certificateModalFormValidation from '../validation/certificate-modal-form-validation';

interface CertificateModalViewProps {
  add: boolean,
  formTitle: string,
  certificate: ICertificate | null | undefined,
  setCertificate: (certificate: ICertificate) => void
}

const getDefaultCertificateValues = (certificate?: ICertificate | null): ICertificate => ({
  title: certificate?.title ?? '',
  description: certificate?.description ?? '',
  template: certificate?.template ?? CertificateTemplate.CERT_TEMPLATE_1,
  header: certificate?.header ?? '',
  logos: certificate?.logos ?? [],
  badge: certificate?.badge,
  signatories: certificate?.signatories ?? [],
  validity: certificate?.validity,
});

const sanitizeCertificate = (certificate: ICertificate): ICertificate => ({
  ...certificate,
  logos: certificate.logos && certificate.logos.length > 0 ? certificate.logos : undefined,
  badge: certificate.badge && (certificate.badge.title || certificate.badge.imageUrl)
    ? certificate.badge
    : undefined,
  signatories: certificate.signatories && certificate.signatories.length > 0
    ? certificate.signatories
    : undefined,
  validity: certificate.validity && certificate.validity.value
    ? certificate.validity
    : undefined,
});

export default function CertificateModalView({
  add, formTitle, certificate, setCertificate,
}: CertificateModalViewProps) {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const methods = useForm<ICertificate>({
    defaultValues: getDefaultCertificateValues(certificate),
    resolver: yupResolver(certificateModalFormValidation),
    shouldFocusError: true,
  });

  const handleClose = React.useCallback(() => {
    methods.reset(getDefaultCertificateValues(certificate));
    setOpen(false);
  }, [certificate, methods]);

  React.useEffect(() => {
    methods.reset(getDefaultCertificateValues(certificate));
  }, [certificate, methods]);

  const onSubmit: SubmitHandler<ICertificate> = (data) => {
    setCertificate(sanitizeCertificate(data));
    setOpen(false);
  };

  return (
    <>
      {add ? (
        <PrimaryOutlinedButton
          onClick={handleOpen}
          startIcon={<AddIcon />}
        >
          Add certificate
        </PrimaryOutlinedButton>
      ) : (
        <PrimaryOutlinedButton
          onClick={handleOpen}
          startIcon={<EditIcon />}
        >
          Edit
        </PrimaryOutlinedButton>
      )}

      <Modal open={open}>
        <Box className="modal-scrollable">
          <IconButton aria-label="close" sx={{ float: 'right' }} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <FormProvider {...methods}>
            <CertificateModalForm
              title={formTitle}
              onSubmit={onSubmit}
              loading={false}
              closeModal={handleClose}
            />
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
}
